import React, { useContext, useState } from 'react';
import { View, Text } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import moment from 'moment';

import AuthContext from 'app/src/contexts/AuthContext';
import Input from 'app/src/elements/inputs';
import Modal from 'app/src/elements/Modal';
import { Button, ButtonGroup, ButtonContainer } from 'app/src/elements/buttons';
import { Warning, WarningText } from 'app/src/styles';

import { REVIEW_TAX_EXEMPTION_REQUEST } from './queries';
import { SectionCard, SectionHeader } from './elements';

export const TaxExemptionDecisions = { VERIFY: 'VERIFY', REJECT: 'REJECT' };

const Statuses = {
  PENDING: 'Pending',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
};

// Browsers other than Safari can't render HEIC, so those link by name.
const THUMBNAIL_CONTENT_TYPES = ['image/jpeg', 'image/png'];

const formatTime = value => moment(value).format('MM-DD-YY @ hh:mmA');

const ProofFile = ({ file }) => (
  <a href={file.url} target="_blank" rel="noreferrer">
    {THUMBNAIL_CONTENT_TYPES.includes(file.contentType) ? (
      <img
        src={file.url}
        alt={file.filename}
        style={{ maxWidth: 200, maxHeight: 200, marginRight: 10 }}
      />
    ) : file.filename}
  </a>
);

const Outcome = ({ request }) => {
  if (Statuses.PENDING === request.status) { return null; }

  const reason = Statuses.REJECTED === request.status
    ? ` — ${request.rejectionReason}`
    : '';

  return (
    <Text>
      {`${request.status} by ${request.reviewedBy?.fullName} on ${formatTime(request.reviewedAt)}${reason}`}
    </Text>
  );
};

const exemptionSummary = (buyer, requests) => {
  if (!buyer.taxExempt) { return 'Tax exempt: No'; }

  const verified = requests.find(request => (
    Statuses.VERIFIED === request.status
  ));

  return verified
    ? `Tax exempt: Yes (verified by ${verified.reviewedBy?.fullName} on ${formatTime(verified.reviewedAt)})`
    : 'Tax exempt: Yes (set manually)';
};

const RejectForm = ({ onReject }) => {
  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { rejectionReason: '' },
  });

  return (
    <View>
      <Input.Text
        label="Reason:"
        name="rejectionReason"
        control={control}
        errors={errors}
        rules={{ required: true }}
        multiline
        rows={4}
      />
      <ButtonContainer>
        <ButtonGroup>
          <Button
            label="Reject Proof"
            onPress={handleSubmit(
              ({ rejectionReason }) => onReject(rejectionReason)
            )}
          />
        </ButtonGroup>
      </ButtonContainer>
    </View>
  );
};

const ProofFiles = ({ request }) => (
  <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginVertical: 10 }}>
    {request.proofFiles.map(file => <ProofFile key={file.id} file={file} />)}
  </View>
);

const TaxExemption = ({ buyer, refetch }) => {
  const { currentUser } = useContext(AuthContext);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [reviewError, setReviewError] = useState();

  const [reviewTaxExemptionRequest] = useMutation(
    REVIEW_TAX_EXEMPTION_REQUEST,
    {
      onCompleted: data => {
        const errors = data?.reviewTaxExemptionRequest?.errors || [];
        setReviewError(errors.map(error => error.message).join(' '));
        setShowRejectModal(false);
        refetch();
      },
    },
  );

  const requests = buyer.taxExemptionRequests || [];
  const [latest, ...earlier] = requests;
  if (!latest && !buyer.taxExempt) { return null; }

  const isPending = Statuses.PENDING === latest?.status;

  const review = (decision, rejectionReason) => {
    const variables = { id: latest.id, decision };
    if (rejectionReason) { variables.rejectionReason = rejectionReason; }
    reviewTaxExemptionRequest({ variables });
  };

  return (
    <SectionCard>
      <SectionHeader>Tax Exemption</SectionHeader>
      <Text style={{ marginBottom: 10 }}>
        {exemptionSummary(buyer, requests)}
      </Text>
      {latest && (
        <>
          <Text style={{ fontWeight: 'bold' }}>
            {isPending ? 'Pending review' : latest.status}
          </Text>
          <Text>
            {`Submitted by ${latest.submittedBy?.fullName} (${latest.submittedBy?.email})`}
          </Text>
          <Text>{formatTime(latest.createdAt)}</Text>
          <ProofFiles request={latest} />
          <Outcome request={latest} />
        </>
      )}

      {!!reviewError && (
        <Warning>
          <WarningText>{`Review not saved: ${reviewError}`}</WarningText>
        </Warning>
      )}

      {isPending && currentUser?.canApproveBuyer && (
        <>
          <Button
            label="Verify"
            style={{ width: 300, backgroundColor: 'green' }}
            onPress={() => review(TaxExemptionDecisions.VERIFY)}
          />
          <Button
            label="Reject"
            style={{ width: 300 }}
            onPress={() => setShowRejectModal(true)}
          />
        </>
      )}

      {earlier.length > 0 && (
        <View style={{ marginTop: 10 }}>
          <Button
            label={
              showHistory
                ? 'Hide earlier requests'
                : `Show earlier requests (${earlier.length})`
            }
            onPress={() => setShowHistory(!showHistory)}
          />
          {showHistory && earlier.map(request => (
            <View key={request.id} style={{ marginTop: 10 }}>
              <Text>{`Submitted ${formatTime(request.createdAt)}`}</Text>
              <Outcome request={request} />
              <ProofFiles request={request} />
            </View>
          ))}
        </View>
      )}

      {showRejectModal && (
        <Modal
          showModal={showRejectModal}
          setShowModal={setShowRejectModal}
          modalType="Reject tax exemption proof"
        >
          <RejectForm
            onReject={reason => review(TaxExemptionDecisions.REJECT, reason)}
          />
        </Modal>
      )}
    </SectionCard>
  );
};

export default TaxExemption;
